import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../../i18n/index.js';
import { playSound } from '../../audio/sounds.js';
import { TurnStatus } from '../common/TurnStatus.js';
import {
  OTHELLO_COLUMNS,
  OTHELLO_SIZE,
  idx,
  othelloLabel,
  type Disc,
  type OthelloLastMove,
  type OthelloView,
} from '../../../shared/games/othello/index.js';
import type { ArenaResult, GameStateView } from '../../../shared/protocol.js';
import type { ArenaSocket } from '../../realtime.js';

function DiscIcon(props: { color: Disc; size?: number }) {
  return <span class={`disc disc-${props.color}`} style={{ width: props.size, height: props.size }} aria-hidden="true" />;
}

/** Othello UI (suunnitelma.md section 42). */
export function OthelloGame(props: { state: GameStateView<OthelloView>; socket: ArenaSocket; serverNow: number }) {
  const { state, socket } = props;
  const view = state.module;
  const myTurn = state.turn?.userId === state.me.id;
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const last = state.lastMove?.data as OthelloLastMove | undefined;
  const lastByMe = state.lastMove?.byUserId === state.me.id;
  const myColor = view.myColor;
  const theirColor: Disc | null = myColor ? (myColor === 'b' ? 'w' : 'b') : null;

  const lastKey = last ? `${last.x},${last.y}` : '';
  const first = useRef(true);
  useEffect(() => {
    if (!first.current && last) playSound('place');
    first.current = false;
  }, [lastKey]);

  const flipped = new Set(last?.flipped.map((c) => idx(c.x, c.y)) ?? []);
  const legal = new Set(myTurn ? view.legalMoves.map((c) => idx(c.x, c.y)) : []);

  const play = (x: number, y: number) => {
    if (!myTurn || pending || !legal.has(idx(x, y))) return;
    setPending(true);
    setError(null);
    socket.emit('game:move', { x, y }, (r: ArenaResult) => {
      setPending(false);
      if (!r.ok) setError(t.arena.errors[r.error]);
    });
  };

  const passNote = last?.opponentPassed ? (lastByMe ? t.othello.passed : t.othello.youPassed) : null;
  const colorName = (c: Disc) => (c === 'b' ? t.othello.black : t.othello.white);

  return (
    <div class="othello">
      <TurnStatus state={state} serverNow={props.serverNow}>
        {passNote && <span class="last-shot shot-miss">{passNote}</span>}
      </TurnStatus>
      {!state.opponent.online && <div class="conn-banner">{t.battleship.opponentOffline}</div>}
      {error && <p class="error">{error}</p>}

      <div class="othello-layout">
        <div class="othello-board-wrap">
          <div class="othello-rows" aria-hidden="true">
            {Array.from({ length: OTHELLO_SIZE }, (_, i) => (
              <span>{i + 1}</span>
            ))}
          </div>
          <div class={`othello-board ${myTurn && !pending ? 'active' : ''}`}>
            {Array.from({ length: OTHELLO_SIZE * OTHELLO_SIZE }, (_, i) => {
              const x = i % OTHELLO_SIZE;
              const y = Math.floor(i / OTHELLO_SIZE);
              const disc = view.board[i];
              const isLast = last && last.x === x && last.y === y;
              return (
                <button
                  class={`o-cell ${legal.has(i) ? 'legal' : ''} ${isLast ? 'last' : ''}`}
                  disabled={!legal.has(i) || pending}
                  aria-label={`${t.othello.cell(othelloLabel({ x, y }))}${disc ? ': ' + colorName(disc) : ''}`}
                  onClick={() => play(x, y)}
                >
                  {disc && (
                    <span
                      key={`${i}-${disc}`}
                      class={`disc disc-${disc} ${flipped.has(i) ? 'flipping' : ''} ${isLast ? 'placed' : ''}`}
                    />
                  )}
                </button>
              );
            })}
          </div>
          <span />
          <div class="othello-cols" aria-hidden="true">
            {OTHELLO_COLUMNS.split('').map((c) => (
              <span>{c}</span>
            ))}
          </div>
        </div>

        <aside class="card othello-side">
          <div class="score-row">
            {myColor && <DiscIcon color={myColor} size={22} />}
            <span class="score-name">{t.othello.you}</span>
            <strong class="score-value">{myColor ? view.count[myColor] : 0}</strong>
          </div>
          <div class="score-row">
            {theirColor && <DiscIcon color={theirColor} size={22} />}
            <span class="score-name">{state.opponent.displayName}</span>
            <strong class="score-value">{theirColor ? view.count[theirColor] : 0}</strong>
          </div>
          {myColor && <p class="muted">{t.othello.yourColor(colorName(myColor))}</p>}
        </aside>
      </div>
    </div>
  );
}
